import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Col, Row } from 'react-bootstrap';
import { format, parseISO, isBefore, endOfToday } from 'date-fns';
import Loading from '../../../../components/Loading';
import { IOption } from '../../../../components/SelectSync';
import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';
import api from '../../../../services/api';
import IMotivo from '../../../../utils/interface/IMotivo';
import CardCadastro from './CardCadastro';
import { ICadastroCobranca } from '..';

import { IDependente, IParcelas } from '../../../CartaoSaudeDetalhe';
import { IContaPR } from '../../../../components/Pagamento';
import { formatPrice, formataTelefone } from '../../../../utils/funcoes';
import getResponseErrors from '../../../../utils/getResponseErrors';
import Formulario, { IRetornoCobranca } from './Formulario';

import Boleto from '../../../../components/Pagamento/Boleto';

// import { Container } from './styles';
interface IPlano {
  PlaCodigo: number;
  PlaNumero: string;
  PlaDataInicio: Date;
  PlaInicioFormatado: string;
}

interface ITitular extends IDependente {
  parcelasPagas: number;
}

export interface IParcelasCobranca extends IContaPR {
  CprDataVencFormatada: string;
  CprValorFormatado: string;
  selecionado: boolean;
}

type Props = {
  dadosCobranca: ICadastroCobranca;
  handleFechar(plaCodigo: number | undefined): void;
};

const Cadastro: React.FC<Props> = ({ dadosCobranca, handleFechar }) => {
  const { addToast } = useToast();
  const { clinica } = useAuth();
  const [loading, setLoading] = useState(false);
  const [optionsMotivo, setOptionsMotivo] = useState<IOption[]>([]);
  const [cobrancas, setCobrancas] = useState<IRetornoCobranca[]>([]);
  const [plano, setPlano] = useState<IPlano | undefined>(undefined);
  const [dependentes, setDependentes] = useState<IDependente[] | undefined>(
    undefined,
  );
  const [titular, setTitular] = useState<ITitular | undefined>(undefined);

  const [parcelas, setParcelas] = useState<IParcelasCobranca[]>([]);

  const [podeGerarBoleto, setPodeGerarBoleto] = useState(false);

  const existeBoleto = useMemo(() => {
    const temBoleto =
      cobrancas.find(cob => cob.parcela && !cob.parcela.CprPago) !== undefined;

    return (
      temBoleto &&
      (clinica.ConfigBoleto?.Emite || clinica.ConfigBoleto?.EmiteOnLine)
    );
  }, [cobrancas, clinica]);

  const loadCobrancas = useCallback(
    async (plaCodigo: number): Promise<void> => {
      setLoading(true);
      try {
        const responseMotivo = await api.get<IMotivo[]>(
          `/motivo?MotAtivo=true&MotTipo=3`,
        );

        const motivos = responseMotivo.data.sort((a, b) => {
          // eslint-disable-next-line no-nested-ternary
          return a.MotMotivo < b.MotMotivo
            ? -1
            : a.MotMotivo > b.MotMotivo
            ? 1
            : 0;
        });
        const options = motivos.map((op: IMotivo) => ({
          value: op.MotCodigo.toString(),
          label: op.MotMotivo,
        }));

        setOptionsMotivo(options);
        const response = await api.get(`/planos/${plaCodigo}`);

        const { PlaCodigo, PlaNumero, PlaDataInicio } = response.data;
        const dadosPlano: IPlano = {
          PlaCodigo,
          PlaNumero,
          PlaDataInicio,
          PlaInicioFormatado: format(
            parseISO(PlaDataInicio.toString()),
            'dd/MM/yyyy',
          ),
        };

        setPlano(dadosPlano);

        const retornoCobranca: IRetornoCobranca[] = response.data.cobrancas.map(
          (item: IRetornoCobranca): IRetornoCobranca => {
            const parcelaEncontrada = response.data.parcelas.find(
              (parc: IParcelas) =>
                parc.CprCodigo === item.CobCprCodigoBoleto &&
                parc.CprExcluida === false &&
                parc.CprParcelaAtiva === true,
            );

            const parcela = parcelaEncontrada
              ? {
                  ...parcelaEncontrada,
                  CprFormaPag: 1,
                  CprReceita: 1,
                  CprUsuCodigo: 1,
                }
              : undefined;
            return {
              ...item,
              CobDataAvisoFormatada: item.CobDataAviso
                ? format(parseISO(item.CobDataAviso.toString()), 'dd/MM/yyyy')
                : '',
              CobDataFormatada: item.CobData
                ? format(parseISO(item.CobData?.toString()), 'dd/MM/yyyy')
                : '',
              parcela,
            };
          },
        );

        setCobrancas(retornoCobranca);

        const dadosTitular: IDependente[] = response.data.dependentes.filter(
          (dep: IDependente) => dep.DepTitular === true && !dep.DepDataExclusao,
        );

        const parcelasPagas = response.data.parcelas.filter(
          (parc: IContaPR) =>
            parc.CprPago === true && parc.CprParcelaAtiva === true,
        ).length;

        if (dadosTitular[0])
          setTitular({
            ...dadosTitular[0],
            parcelasPagas,
          });

        const parcelasEmAtraso: IContaPR[] = response.data.parcelas.filter(
          (parc: IContaPR) =>
            parc.CprPago === false &&
            parc.CprExcluida === false &&
            parc.CprParcelaAtiva === true &&
            isBefore(parseISO(parc.CprDataVenc.toString()), endOfToday()),
        );

        const parcelasFormatadas = parcelasEmAtraso.map(parc => {
          return {
            ...parc,
            CprDataVencFormatada: format(
              parseISO(parc.CprDataVenc.toString()),
              'dd/MM/yyyy',
            ),
            CprValorFormatado: formatPrice(parc.CprValor),
            selecionado: true,
          };
        });

        setParcelas(parcelasFormatadas);

        const dadosDependentes: IDependente[] = response.data.dependentes.filter(
          (dep: IDependente) =>
            dep.DepTitular === false && !dep.DepDataExclusao,
        );

        setDependentes(dadosDependentes);

        const responsavel = dadosDependentes.find(dep => dep.DepResponsavel);

        let temCPF =
          responsavel && responsavel.paciente.PacPessoaFisica
            ? responsavel.paciente.PacCPF !== undefined &&
              responsavel.paciente.PacCPF !== ''
            : responsavel?.paciente.PacCNPJ !== undefined &&
              responsavel?.paciente.PacCNPJ !== '';
        if (!temCPF && dadosTitular[0])
          temCPF = dadosTitular[0].paciente.PacPessoaFisica
            ? dadosTitular[0].paciente.PacCPF !== undefined &&
              dadosTitular[0].paciente.PacCPF !== ''
            : dadosTitular[0]?.paciente.PacCNPJ !== undefined &&
              dadosTitular[0]?.paciente.PacCNPJ !== '';

        setPodeGerarBoleto(temCPF);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      }
      setLoading(false);
    },
    [addToast],
  );

  useEffect(() => {
    if (dadosCobranca) loadCobrancas(dadosCobranca.plaCodigo);
  }, [dadosCobranca, loadCobrancas]);

  return (
    <>
      {loading && <Loading backgroundBlack={false} />}

      {/* {showBoletoNegociacao && (
        <Modal
          show={showBoletoNegociacao}
          titulo="Gerar Boleto Negociação"
          handleFecharModal={() => setShowBoletoNegociacao(false)}
          large
        >
          <Formulario
            handleFechar={handleFechar}
            dadosCobranca={dadosCobranca}
            optionsMotivo={optionsMotivo}
            tipo="kjlhjkl"
          />
        </Modal>
      )} */}
      <Row>
        <Col xl="4" md="4">
          <CardCadastro title="CARTÃO">
            <b>
              <h4>{plano?.PlaNumero}</h4>
            </b>
            <span className="Small">ADESÃO {plano?.PlaInicioFormatado}</span>
          </CardCadastro>
        </Col>
        <Col xl="4" md="4">
          <CardCadastro title="TITULAR">
            {titular && (
              <>
                <b>
                  {titular.paciente.PacNomeSocial !== ''
                    ? `Nome Social: ${titular.paciente.PacNomeSocial} - ${titular.paciente.PacNome}`
                    : titular.paciente.PacNome}
                </b>
                <br />
                <span style={{ fontSize: 'Smaller' }}>
                  {titular?.paciente.PacTelefone
                    ? `Tel. ${formataTelefone(titular?.paciente.PacTelefone)}`
                    : ''}{' '}
                  {titular?.paciente.PacCelular
                    ? `Cel. ${formataTelefone(titular?.paciente.PacCelular)}`
                    : ''}
                </span>
                <br />
                Total de parcelas pagas: <b>{titular?.parcelasPagas}</b>
              </>
            )}
          </CardCadastro>
        </Col>
        <Col xl="4" md="4">
          {dependentes && dependentes.length > 0 && (
            <CardCadastro title="DEPENDENTES">
              {dependentes.map(dep => (
                <div
                  key={`dep-${dep.DepCodigo}-${dep.DepPlaCodigo}`}
                  className={dep.DepResponsavel ? 'text-danger' : ''}
                >
                  {dep.DepResponsavel ? <b>RESPONSÁVEL: </b> : ''}
                  <b>
                    {dep.paciente.PacNomeSocial !== ''
                      ? `Nome Social: ${dep.paciente.PacNomeSocial} - ${dep.paciente.PacNome}`
                      : dep.paciente.PacNome}
                  </b>
                  <br />
                  <span style={{ fontSize: 'Smaller' }}>
                    {dep?.paciente.PacTelefone
                      ? `Tel. ${formataTelefone(dep?.paciente.PacTelefone)}`
                      : ''}{' '}
                    {dep?.paciente.PacCelular
                      ? `Cel. ${formataTelefone(dep?.paciente.PacCelular)}`
                      : ''}
                  </span>
                  <br />
                </div>
              ))}
            </CardCadastro>
          )}
        </Col>
      </Row>
      <br />
      <Row>
        <Col>
          <CardCadastro title="NEGOCIAÇÕES ANTERIORES">
            <div
              style={{ maxHeight: '114px', overflowY: 'auto', margin: '-15px' }}
            >
              {cobrancas && cobrancas.length > 0 && (
                <table
                  className="table table-hover table-sm small"
                  cellSpacing="0"
                  cellPadding="4"
                  style={{ borderCollapse: 'collapse', fontFamily: 'verdana' }}
                >
                  <tbody>
                    <tr className="bg-light text-info small">
                      <th scope="col">Dt. Contato</th>
                      <th scope="col">Colaborador</th>
                      <th scope="col">Resposta do Cliente</th>
                      <th scope="col">dt. Novo Contato</th>
                      <th> - </th>
                    </tr>
                    {cobrancas.map(item => (
                      <tr
                        key={`cobItem-${item.CobCodigo}`}
                        style={{ fontSize: 'Smaller' }}
                      >
                        <td style={{ width: '85px' }}>
                          {item.CobDataFormatada}
                        </td>
                        <td>{item.CobUsuNome}</td>
                        <td>{item.CobObservacao}</td>
                        <td style={{ width: '110px' }}>
                          {item.CobDataAvisoFormatada}
                        </td>
                        <td>
                          {/* {item.CobLink && item.CobLink !== '' && (
                            <a
                              href={item.CobLink || ''}
                              title="Ver Boleto"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ width: '34px' }}
                              // className="btn btn-sm btn-outline-primary"
                            >
                              <FaBarcode
                                size="20"
                                color={`${Colors.primary}`}
                              />
                            </a>
                          )} */}
                          {item.parcela && !item.parcela?.CprPago && (
                            <>
                              <div className="d-flex justify-content-end">
                                <Boleto
                                  cprCodigo={item.CobCprCodigoBoleto}
                                  contaPR={item.parcela}
                                  inLine
                                />
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardCadastro>
        </Col>
      </Row>
      <br />
      <Row>
        <Col>
          <CardCadastro title="NOVA NEGOCIAÇÃO">
            <div
              className="text-left"
              style={{ fontSize: '.8rem', margin: '-10px' }}
            >
              <Formulario
                handleFechar={handleFechar}
                dadosCobranca={dadosCobranca}
                optionsMotivo={optionsMotivo}
                parcelas={parcelas}
                atualizaDadosCobranca={loadCobrancas}
                temBoleto={existeBoleto}
                podeGerarBoleto={podeGerarBoleto}
              />
            </div>
          </CardCadastro>
        </Col>
      </Row>
    </>
  );
};

export default Cadastro;
